/**
 * Topbar: logo, til tanlash, Bildirishnomalar, Tema, Profil.
 */

import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/authContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useQueryWarehouseOverview } from '../../hooks/useQueryWarehouseOverview';
import { daysUntilExpiry } from '../../utils/productExpiry';
import { getSubscriptionStatus } from '../../services/subscriptionApi';
import {
  getChatPushNotificationsForUser,
  getUnreadChatPushCount,
  markChatPushRead,
  formatChatPushTime,
  subscribeChatPushInbox,
  type ChatPushRecord,
} from '../../utils/chatPushNotifications';
import { useNotifications } from '../../hooks/useNotifications';
import { markNotificationRead } from '../../services/notificationsApi';
import { ProfileModal } from './ProfileModal';
import { RoleSwitchModal } from './RoleSwitchModal';
import { TariffPlanModal } from './TariffPlanModal';
import { CashierSubscriptionReminderModal } from './CashierSubscriptionReminderModal';
import { isSubscriptionPastGraceDay } from '../../utils/subscriptionGrace';
import { LanguageDropdown } from '../LanguageDropdown';
import { isAdminImpersonationActive } from '../../utils/adminImpersonationStorage';
import { getCurrencyRates } from '../../services/systemApi';
import './Topbar.css';

import { getStoredSiteTheme, setStoredSiteTheme } from '../../utils/siteTheme';

const TOPBAR_CURRENCY_KEY = 'admin-topbar-currency';
const TOPBAR_CURRENCY_FALLBACK_CODES = ['USD', 'EUR', 'RUB', 'KZT', 'TRY', 'CNY'] as const;

function getStoredTopbarCurrency(): string {
  try {
    const raw = localStorage.getItem(TOPBAR_CURRENCY_KEY);
    if (raw && String(raw).trim()) return String(raw).trim();
  } catch {}
  return 'USD';
}

function topbarChatNavPath(role: string | undefined, n: ChatPushRecord): string {
  if (role === 'tenant_owner') return '/admin/help';
  if (role === 'dispatcher') return '/admin/notifications';
  return `/admin/tenant-messages?tenant=${encodeURIComponent(n.tenantId)}`;
}

interface TopbarProps {
  sidebarOpen?: boolean;
  onMenuClick: () => void;
}

interface TopbarApiNotification {
  id: string;
  read: boolean;
  titleKey: string;
  bodyKey: string;
  title?: string | null;
  body?: string | null;
  timestamp: string;
  linkPath?: string | null;
}

const ADMIN_IMPERSONATION_ROLES = new Set([
  'platform_owner',
  'general_manager',
  'regional_manager',
  'tenant_owner',
]);

export function Topbar({ sidebarOpen = false, onMenuClick }: TopbarProps) {
  const { user, baseUser, logout } = useAuth();
  const navigate = useNavigate();
  const { t, switchLanguage, availableLanguages, language } = useLanguage();
  const tSafe = useCallback((key: string, fallback: string) => {
    const translated = t(key);
    if (translated == null) return fallback;
    const cleaned = String(translated).trim();
    if (!cleaned || cleaned === key) return fallback;
    return cleaned;
  }, [t]);
  const isMijozTopbar = user?.role === 'tenant_owner';
  const isCashierTopbar = user?.role === 'cashier';
  const pollTenantSubscription = isMijozTopbar || isCashierTopbar;
  const { data: whOverviewRaw = [] } = useQueryWarehouseOverview(
    isMijozTopbar ? { role: 'tenant_owner', include_batches: 0 } : { _skip: true },
  );
  const [dismissedExpiryKeys, setDismissedExpiryKeys] = useState<string[]>([]);
  const expiryAlerts = useMemo(() => {
    if (!isMijozTopbar || !Array.isArray(whOverviewRaw)) return [];
    return (whOverviewRaw as Array<{ productName?: string; branchName?: string; expiryDate?: string | null }>)
      .map((r) => {
        const d = daysUntilExpiry(r.expiryDate ?? null);
        if (d === null || d > 3) return null;
        const name = r.productName ?? '—';
        const branch = r.branchName ?? '—';
        return {
          key: `${name}__${branch}`,
          name,
          branch,
          days: d,
        };
      })
      .filter(Boolean)
      .filter((r) => r != null && !dismissedExpiryKeys.includes(r.key)) as { key: string; name: string; branch: string; days: number }[];
  }, [isMijozTopbar, whOverviewRaw, dismissedExpiryKeys]);
  const [theme, setTheme] = React.useState<'light' | 'dark'>(getStoredSiteTheme);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);

  // Obuna holati (faqat tenant_owner)
  const [subStatus, setSubStatus] = useState<{
    status: string; daysUntilExpiry: number; isTrial: boolean; isExpired: boolean; endsAt: string | null; monthlyPrice: number;
  } | null>(null);
  const [subModalOpen, setSubModalOpen] = useState(false);
  /** Sessiya ichida muddati tugaganda tarif modali bir marta avtomatik ochiladi (grace davrida yopish mumkin). */
  const tariffAutoOpenedForExpiryRef = useRef(false);
  const [currencyRates, setCurrencyRates] = useState<Record<string, number>>({ UZS: 1 });
  const [currencyUpdatedAt, setCurrencyUpdatedAt] = useState<string>('');
  const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<string>(getStoredTopbarCurrency);
  // Legacy inline payment modal removed (now handled by TariffPlanModal)

  useEffect(() => {
    if (!pollTenantSubscription) return;
    let cancelled = false;
    const load = () => {
      getSubscriptionStatus()
        .then((d) => {
          if (!cancelled) setSubStatus(d);
        })
        .catch(() => {});
    };
    load();
    const tmr = window.setInterval(load, 120000);
    return () => {
      cancelled = true;
      window.clearInterval(tmr);
    };
  }, [pollTenantSubscription]);

  useEffect(() => {
    if (!isMijozTopbar || !subStatus) return;
    if (!subStatus.isExpired) {
      tariffAutoOpenedForExpiryRef.current = false;
      return;
    }
    if (!tariffAutoOpenedForExpiryRef.current) {
      setSubModalOpen(true);
      tariffAutoOpenedForExpiryRef.current = true;
    }
  }, [isMijozTopbar, subStatus]);

  const subscriptionGraceActive = isSubscriptionPastGraceDay(subStatus);
  useEffect(() => {
    if (!pollTenantSubscription || !subscriptionGraceActive) return;
    const id = window.setInterval(() => {
      getSubscriptionStatus()
        .then((d) => setSubStatus(d))
        .catch(() => {});
    }, 12000);
    return () => window.clearInterval(id);
  }, [pollTenantSubscription, subscriptionGraceActive]);

  const forceOwnerTariffLock = false;
  const forceCashierSubscriptionReminder = isCashierTopbar && subscriptionGraceActive;

  useEffect(() => {
    let cancelled = false;
    const loadRates = async () => {
      try {
        const data = await getCurrencyRates();
        if (cancelled) return;
        setCurrencyRates(data.rates || { UZS: 1 });
        setCurrencyUpdatedAt(data.updatedAt || '');
      } catch {
        // keep last value
      }
    };
    void loadRates();
    const timer = window.setInterval(() => {
      void loadRates();
    }, 600000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(TOPBAR_CURRENCY_KEY, selectedCurrencyCode);
    } catch {}
  }, [selectedCurrencyCode]);

  const availableCurrencyCodes = useMemo(() => {
    const keys = Object.keys(currencyRates ?? {}).filter((k) => k && k.toUpperCase() !== 'UZS');
    const uniq = Array.from(new Set(keys.map((k) => k.toUpperCase()))).sort((a, b) => a.localeCompare(b));
    return uniq.length > 0 ? uniq : Array.from(TOPBAR_CURRENCY_FALLBACK_CODES);
  }, [currencyRates]);

  useEffect(() => {
    if (!availableCurrencyCodes.includes(String(selectedCurrencyCode).toUpperCase())) {
      setSelectedCurrencyCode(availableCurrencyCodes[0] || 'USD');
    }
  }, [availableCurrencyCodes, selectedCurrencyCode]);

  // (payment is handled inside TariffPlanModal)
  const [pushInboxV, setPushInboxV] = useState(0);
  useEffect(() => subscribeChatPushInbox(() => setPushInboxV((x) => x + 1)), []);

  const chatPushSlice = useMemo(
    () => (user ? getChatPushNotificationsForUser(user).slice(0, 12) : []),
    [user, pushInboxV],
  );
  const unreadChatPush = useMemo(
    () => (user ? getUnreadChatPushCount(user) : 0),
    [user, pushInboxV],
  );
  const role = user?.role;
  const notificationsApiEnabled =
    role === 'dispatcher' ||
    role === 'regional_manager' ||
    role === 'general_manager' ||
    role === 'platform_owner';
  const { data: apiNotificationsRaw = [], refetch: refetchApiNotifications } = useNotifications({
    enabled: notificationsApiEnabled,
    pollIntervalMs: notificationsApiEnabled ? 60000 : 0,
  });
  const apiNotifications = useMemo(
    () => (Array.isArray(apiNotificationsRaw) ? (apiNotificationsRaw as TopbarApiNotification[]) : []),
    [apiNotificationsRaw],
  );
  const unreadApiNotifications = useMemo(
    () => (Array.isArray(apiNotifications) ? apiNotifications.filter((n) => !n.read).length : 0),
    [apiNotifications],
  );
  const topbarApiSlice = useMemo(
    () => (Array.isArray(apiNotifications) ? apiNotifications.slice(0, 8) : []),
    [apiNotifications],
  );
  useEffect(() => {
    if (!notificationsApiEnabled) return undefined;
    const handleNotificationsUpdate = () => {
      refetchApiNotifications();
    };
    window.addEventListener('admin-notifications-updated', handleNotificationsUpdate);
    return () => window.removeEventListener('admin-notifications-updated', handleNotificationsUpdate);
  }, [notificationsApiEnabled, refetchApiNotifications]);
  const showBellBadge =
    unreadChatPush > 0 ||
    unreadApiNotifications > 0 ||
    (isMijozTopbar && expiryAlerts.length > 0);
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [profileModalOpen, setProfileModalOpen] = React.useState(false);
  const [roleSwitchModalOpen, setRoleSwitchModalOpen] = React.useState(false);
  const showAdminImpersonateEntry =
    !isAdminImpersonationActive() && !!baseUser?.role && ADMIN_IMPERSONATION_ROLES.has(baseUser.role);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);


  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    setStoredSiteTheme(theme);
  }, [theme]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (notificationsRef.current && !notificationsRef.current.contains(target)) setNotificationsOpen(false);
      if (profileRef.current && !profileRef.current.contains(target)) setProfileOpen(false);
      if (moreRef.current && !moreRef.current.contains(target)) setMoreOpen(false);
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNotificationsOpen(false);
        setProfileOpen(false);
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const toggleTheme = () => {
    setTheme((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const closeAllDropdowns = () => {
    setNotificationsOpen(false);
    setProfileOpen(false);
    setMoreOpen(false);
  };

  const openExpiryTracking = (alertKey: string) => {
    setDismissedExpiryKeys((prev) => (prev.includes(alertKey) ? prev : [...prev, alertKey]));
    navigate('/admin/expiry-tracking');
    setNotificationsOpen(false);
  };

  const openNotificationsFromMore = () => {
    setMoreOpen(false);
    setNotificationsOpen(true);
  };
  const selectedRate = Number(currencyRates[selectedCurrencyCode] ?? 0);
  const publicUrl = process.env.PUBLIC_URL || '';
  const topbarLogoSrc =
    theme === 'dark' ? `${publicUrl}/adde-logo-dark.png` : `${publicUrl}/adde-logo.png`;

  return (
    <>
      <header className="admin-topbar">
        <div className="admin-topbar-inner">
          <button
            type="button"
            className={`admin-topbar-menu ${sidebarOpen ? 'admin-topbar-menu-open' : ''}`}
            onClick={onMenuClick}
            aria-label={sidebarOpen ? t('adminMenuClose') : t('adminMenuOpen')}
            aria-expanded={sidebarOpen}
          >
            {sidebarOpen ? <MenuCloseIcon /> : <MenuIcon />}
          </button>
          <Link to="/" className="admin-topbar-logo">
            <img
              key={topbarLogoSrc}
              src={topbarLogoSrc}
              alt="Add-e"
              className="admin-topbar-logo-img"
              onError={(e) => {
                const img = e.target as HTMLImageElement;
                img.style.display = 'none';
                const fallback = img.nextElementSibling as HTMLElement | null;
                if (fallback) fallback.style.display = 'inline';
              }}
            />
            <span className="admin-topbar-logo-text admin-topbar-logo-fallback" style={{ display: 'none' }} aria-hidden>Add-e</span>
          </Link>

          <div className="admin-topbar-right">
          {/* Tarif rejasi — faqat tenant_owner uchun (modal) */}
          {isMijozTopbar ? (
            <button
              type="button"
              className="admin-topbar-tariff-btn"
              onClick={() => setSubModalOpen(true)}
              aria-label={tSafe('adminNav_tariff_plan', 'Tarif rejasi')}
              title={tSafe('adminNav_tariff_plan', 'Tarif rejasi')}
            >
              {tSafe('adminNav_tariff_plan', 'Tarif rejasi')}
              {subStatus ? (
                <span
                  className={`admin-topbar-tariff-pill admin-topbar-tariff-pill--${subStatus.isExpired ? 'expired' : subStatus.isTrial ? 'trial' : 'active'}`}
                >
                  {subStatus.isExpired
                    ? tSafe('subscription_expired_short', 'Tugagan')
                    : subStatus.daysUntilExpiry > 0
                      ? `${subStatus.daysUntilExpiry} ${tSafe('subscription_days_left', 'kun')}`
                      : tSafe('subscription_expires_today', 'Bugun')}
                </span>
              ) : null}
            </button>
          ) : null}

          {user?.role && (
            <span className="admin-topbar-role" title={t('adminCurrentRole')}>
              {t(`roleTitle_${user.role}`)}
            </span>
          )}
          <div className="admin-topbar-currency" title={currencyUpdatedAt ? `${t('adminTopbar_currency_updated')}: ${new Date(currencyUpdatedAt).toLocaleString()}` : t('adminTopbar_currency_title')}>
            <span className="admin-topbar-currency-label">{t('adminTopbar_currency_title')}</span>
            <div className="admin-topbar-currency-controls">
              <select
                className="admin-topbar-currency-select"
                value={selectedCurrencyCode}
                onChange={(e) => setSelectedCurrencyCode(e.target.value)}
                aria-label={t('adminTopbar_currency_select')}
              >
                {availableCurrencyCodes.map((code) => (
                  <option key={code} value={code}>{code}</option>
                ))}
              </select>
              <span className="admin-topbar-currency-item">
                {selectedRate > 0 ? `1 ${selectedCurrencyCode} = ${selectedRate.toFixed(2)} UZS` : '—'}
              </span>
            </div>
          </div>
          <LanguageDropdown
            className="admin-topbar-lang admin-topbar-lang-desktop"
            fixedMenu
            ariaLabel={t('adminLanguageLabel')}
          />

          {/* Notification Center */}
          <div className="admin-topbar-dropdown-wrap admin-topbar-notifications-desktop" ref={notificationsRef}>
            <button
              type="button"
              className={`admin-topbar-icon-btn ${notificationsOpen ? 'active' : ''} ${showBellBadge ? 'admin-topbar-bell-has-badge' : ''}`}
              onClick={() => { setNotificationsOpen(!notificationsOpen); setProfileOpen(false); }}
              aria-label={t('adminNotifications')}
              aria-expanded={notificationsOpen}
              title={t('adminNotifications')}
            >
              <BellIcon />
            </button>
            {notificationsOpen && (
              <div className="admin-topbar-dropdown admin-topbar-dropdown-notifications" role="dialog" aria-label={t('adminNotifications')}>
                <div className="admin-topbar-dropdown-head">{t('adminNotifications')}</div>
                <div className="admin-topbar-dropdown-body">
                  {topbarApiSlice.length > 0 && (
                    <>
                      <p className="admin-topbar-dropdown-section-title">{t('adminNotifications')}</p>
                      <ul className="admin-topbar-chat-push-list">
                        {topbarApiSlice.map((n) => (
                          <li key={`api_${n.id}`}>
                            <button
                              type="button"
                              className={`admin-topbar-chat-push-row ${n.read ? 'admin-topbar-chat-push-row-read' : ''}`}
                              onClick={async () => {
                                if (!n.read) {
                                  try {
                                    await markNotificationRead(n.id);
                                  } catch {}
                                  refetchApiNotifications();
                                  window.dispatchEvent(new Event('admin-notifications-updated'));
                                }
                                if (n.linkPath) navigate(n.linkPath);
                                setNotificationsOpen(false);
                              }}
                            >
                              <span className="admin-topbar-chat-push-title">{n.title || t(n.titleKey)}</span>
                              <span className="admin-topbar-chat-push-preview">{n.body || t(n.bodyKey)}</span>
                              <span className="admin-topbar-chat-push-time">{n.timestamp}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {chatPushSlice.length > 0 && (
                    <>
                      <p className="admin-topbar-dropdown-section-title">{t('adminTopbar_chat_notifications')}</p>
                      <ul className="admin-topbar-chat-push-list">
                        {chatPushSlice.map((n) => (
                          <li key={n.id}>
                            <button
                              type="button"
                              className={`admin-topbar-chat-push-row ${n.read ? 'admin-topbar-chat-push-row-read' : ''}`}
                              onClick={() => {
                                markChatPushRead(n.id);
                                navigate(topbarChatNavPath(user?.role, n));
                                setNotificationsOpen(false);
                              }}
                            >
                              <span className="admin-topbar-chat-push-title">
                                {n.fromMijoz ? t('notif_chat_from_tenant_owner') : t('notif_chat_from_support')}
                                {n.companyName ? ` — ${n.companyName}` : ''}
                              </span>
                              <span className="admin-topbar-chat-push-preview">{n.preview}</span>
                              <span className="admin-topbar-chat-push-time">{formatChatPushTime(n.createdAt)}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {isMijozTopbar && expiryAlerts.length > 0 && (
                    <>
                      <p className="admin-topbar-expiry-head">{t('adminTopbar_expiry_alerts') || 'Muddati tugashga yaqin mahsulotlar'}</p>
                      <ul className="admin-topbar-expiry-list">
                        {expiryAlerts.slice(0, 15).map((a, i) => (
                          <li key={`${a.key}-${i}`}>
                            <button type="button" className="admin-topbar-expiry-btn" onClick={() => openExpiryTracking(a.key)}>
                              <span className="admin-topbar-expiry-name">{a.name}</span>
                              <span className="admin-topbar-expiry-meta">
                                {a.branch}
                                {' · '}
                                {a.days < 0
                                  ? (t('adminOmbor_expiry_passed') || 'muddati o\'tgan')
                                  : `${a.days} ${t('adminOmbor_expiry_days_left') || 'kun'}`}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {chatPushSlice.length === 0 && topbarApiSlice.length === 0 && !(isMijozTopbar && expiryAlerts.length > 0) && (
                    <p className="admin-topbar-dropdown-empty">{t('adminNoNotifications')}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Theme Switcher */}
          <button
            type="button"
            className="admin-topbar-icon-btn"
            onClick={toggleTheme}
            aria-label={t('adminTheme')}
            title={theme === 'light' ? t('adminThemeDark') : t('adminThemeLight')}
          >
            {theme === 'light' ? <SunIcon /> : <MoonIcon />}
          </button>

          {/* Ro'l almashtirish (tenant_owner ↔ cashier, PIN orqali) */}
          {((baseUser ?? user)?.role === 'tenant_owner' || (baseUser ?? user)?.role === 'cashier') && (
            <button
              type="button"
              className="admin-topbar-icon-btn admin-topbar-role-switch"
              onClick={() => { setRoleSwitchModalOpen(true); closeAllDropdowns(); }}
              aria-label={t('roleSwitch_title') || 'Ro\'l almashtirish'}
              title={t('roleSwitch_title') || 'Ro\'l almashtirish'}
            >
              <RoleSwitchIcon />
            </button>
          )}

          {showAdminImpersonateEntry && (
            <button
              type="button"
              className="admin-topbar-icon-btn admin-topbar-admin-impersonate"
              onClick={() => {
                closeAllDropdowns();
                navigate('/admin/role-access');
              }}
              aria-label={t('sidebar_impersonation_section')}
              title={t('sidebar_impersonation_section')}
            >
              <AdminImpersonateIcon />
            </button>
          )}

          {/* Profile Settings */}
          <div className="admin-topbar-dropdown-wrap" ref={profileRef}>
            <button
              type="button"
              className={`admin-topbar-icon-btn admin-topbar-profile ${profileOpen ? 'active' : ''}`}
              onClick={() => { setProfileOpen(!profileOpen); setNotificationsOpen(false); }}
              aria-label={t('adminProfileSettings')}
              aria-expanded={profileOpen}
              title={t('adminProfileSettings')}
            >
              <ProfileIcon />
            </button>
            {profileOpen && (
              <div className="admin-topbar-dropdown admin-topbar-dropdown-profile" role="menu" aria-label={t('adminProfileSettings')}>
                <div className="admin-topbar-dropdown-head">{t('adminProfileSettings')}</div>
                <div className="admin-topbar-dropdown-body">
                  <button
                    type="button"
                    className="admin-topbar-dropdown-item"
                    onClick={() => {
                      closeAllDropdowns();
                      setProfileModalOpen(true);
                    }}
                  >
                    {t('adminProfile')}
                  </button>
                  <button
                    type="button"
                    className="admin-topbar-dropdown-item admin-topbar-dropdown-item-logout"
                    onClick={() => { closeAllDropdowns(); logout(); }}
                  >
                    {t('adminLogout')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile: "Boshqa" (uch nuqta) – eng oxirida */}
          <div className="admin-topbar-more-wrap" ref={moreRef}>
            <button
              type="button"
              className={`admin-topbar-icon-btn admin-topbar-more-btn ${moreOpen ? 'active' : ''}`}
              onClick={() => { setMoreOpen(!moreOpen); setNotificationsOpen(false); setProfileOpen(false); }}
              aria-label={t('adminMore')}
              aria-expanded={moreOpen}
              title={t('adminMore')}
            >
              <MoreIcon />
            </button>
            {moreOpen && (
              <div className="admin-topbar-dropdown admin-topbar-dropdown-more" role="dialog" aria-label={t('adminMore')}>
                <div className="admin-topbar-dropdown-head">{t('adminMore')}</div>
                <div className="admin-topbar-dropdown-body">
                  <div className="admin-topbar-more-lang">
                    <span className="admin-topbar-more-lang-label" id="admin-more-lang-label">{t('adminLanguageLabel')}</span>
                    <div className="admin-topbar-more-lang-chips" role="group" aria-labelledby="admin-more-lang-label">
                      {availableLanguages.map((lang: { code: string; nativeName: string }) => (
                        <button
                          key={lang.code}
                          type="button"
                          className={`admin-topbar-lang-chip${language === lang.code ? ' admin-topbar-lang-chip--active' : ''}`}
                          onClick={() => {
                            switchLanguage(lang.code);
                            setMoreOpen(false);
                          }}
                        >
                          {lang.code.toUpperCase()}
                        </button>
                      ))}
                    </div>
                    <LanguageDropdown
                      className="admin-topbar-lang admin-topbar-more-lang-select admin-topbar-more-lang-select--desktop"
                      variant="block"
                      fixedMenu
                      onAfterSelect={() => setMoreOpen(false)}
                      ariaLabel={t('adminLanguageLabel')}
                    />
                  </div>
                  <button type="button" className="admin-topbar-dropdown-item" onClick={openNotificationsFromMore}>
                    {t('adminNotifications')}
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </header>
      <ProfileModal isOpen={profileModalOpen} onClose={() => setProfileModalOpen(false)} />
      <RoleSwitchModal isOpen={roleSwitchModalOpen} onClose={() => setRoleSwitchModalOpen(false)} />
      <TariffPlanModal
        isOpen={isMijozTopbar && (subModalOpen || forceOwnerTariffLock)}
        forceLocked={forceOwnerTariffLock}
        onClose={() => {
          if (!forceOwnerTariffLock) setSubModalOpen(false);
        }}
      />
      <CashierSubscriptionReminderModal isOpen={forceCashierSubscriptionReminder} />
    </>
  );
}

function BellIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
    </svg>
  );
}

function ProfileIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

function RoleSwitchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
    </svg>
  );
}

function AdminImpersonateIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function MoreIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </svg>
  );
}

function MenuIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </svg>
  );
}

function MenuCloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
